import { Bar } from "react-chartjs-2";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Align,
  Chart,
  ChartDataset,
  ChartOptions,
  registerables,
} from "chart.js";
import { CapacityChartProps } from "./types";
import { ConnectedProps, connect } from "react-redux";
import { AppDispatch, RootState } from "store";
import { actions } from "reducers/dashboard";
import { actions as modalActions } from "reducers/dashboardSlotCapacityModal";
import { DashboardFilterType } from "reducers/types/dashboard";
import { BasicIcon, ToolTipPosition, Tooltip } from "@pickit/pickit-components";
import "./CapacityChart.scss";
import "../../../pages/dashboard/i18n";
import { useTranslation } from "react-i18next";
import { DashboardDropdownState } from "reducers/types/dashboardSlotCapacityModal";

Chart.register(...registerables);

const CapacityChart = ({
  data,
  barHeight = 10,
  regions,
  shouldOpenDetail,
  previousFilters,
  getDashboard,
  setDropdownState,
  setDashboardFilters,
  getSlotDetail,
}: CapacityChartProps & ConnectorProps) => {
  const { t } = useTranslation();
  const datasetLabels = [
    t("dashboard:withTrx"),
    t("dashboard:withoutTrx"),
    t("dashboard:disconnected"),
  ];

  const reducedRegions = useMemo(
    () => data && data.map((d) => ({ name: d.name, id: d.id })),
    [data]
  );
  const inflateAmount = 1.5;

  const chartHeight = useMemo(
    () => (barHeight + inflateAmount * 2) * data.length * 3 + 100,
    [barHeight, data]
  );

  const longestName = useMemo(
    () =>
      reducedRegions.reduce((a, b) => (a.name.length > b.name.length ? a : b))
        .name,
    [reducedRegions]
  );

  const [labelOffset, setLabelOffset] = useState(0);

  const getBarOptions: (backgroundBar: boolean) => ChartOptions<"bar"> = (
    backgroundBar
  ) => ({
    indexAxis: "y",
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        labels: {
          usePointStyle: true,
          color: backgroundBar ? "transparent" : undefined,
          font: {
            family: "Mulish, sans-serif",
          },
        },
        align: "end",
        onClick: () => {},
      },
      title: {
        text: t("dashboard:slotCapacityTitle"),
        display: true,
        position: "top",
        align: "start",
        color: backgroundBar ? "transparent" : "black",
        font: {
          size: 20,
          family: "Mulish, sans-serif",
          weight: 700,
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: "#F4F4F4",
          opacity: 0.1,
        },
        max: 100,
        stacked: true,
        ticks: {
          color: backgroundBar ? "transparent" : "#2d2d2d66",
          maxTicksLimit: 6,
        },
      },
      y: {
        grid: {
          drawBorder: false,
          display: false,
        },
        border: {
          display: false,
        },
        stacked: true,
        ticks: {
          crossAlign: "far",
          padding: 10,
          color: "transparent",
          font: {
            size: 14,
            family: "Mulish, sans-serif",
          },
        },
      },
    },
  });

  const dataSetConfig: ChartDataset<"bar"> = {
    data: [],
    barThickness: barHeight,
    borderSkipped: false,
    datalabels: {
      display: true,
      color: "#fff",
      anchor: "end",
      align: "start",
      font: {
        size: 14,
        weight: 700,
        family: "Mulish, sans-serif",
      },
    },
  };

  const checkMaxLabelLenght = useCallback(() => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.font = "400 14px Mulish, sans-serif";
      return ctx.measureText(longestName).width;
    }
    return 0;
  }, [longestName]);

  useEffect(() => {
    setLabelOffset(checkMaxLabelLenght());
  }, [checkMaxLabelLenght]);

  const getData = (
    key: "withTransaction" | "withoutTransaction" | "disconnected"
  ) => {
    return data.map((d) => (d.capacity > 0 ? (d[key] / d.capacity) * 100 : ""));
  };

  const alignments: { [key in "align" | "anchor"]: Align } = {
    anchor: "end",
    align: "start",
  };

  const getAlignmet = (
    key: "withTransaction" | "withoutTransaction" | "disconnected",
    index: number,
    type: "align" | "anchor"
  ): Align => {
    return data[index][key] / data[index].capacity < 0.02
      ? "center"
      : alignments[type];
  };

  return regions && regions.length > 0 ? (
    <div className="capacity-chart">
      <div className="capacity-chart-tooltip">
        <Tooltip
          message={t("dashboard:slotCapacityTooltip")}
          position={ToolTipPosition.top}
        >
          <BasicIcon
            name="information-circle"
            size="extra-small"
            color="#0098ff"
          />
        </Tooltip>
      </div>
      <div className="chart-wrapper" style={{ height: chartHeight }}>
        <Bar
          className="data-chart"
          height="100%"
          options={{
            ...getBarOptions(false),
            animation: {
              duration: 0,
            },
            //evento que diferencia click en cada color de cada region
            onClick: (evt, element, chart) => {
              if (element.length > 0 && shouldOpenDetail) {
                setDropdownState(
                  element[0].datasetIndex === 0
                    ? "withTrx"
                    : element[0].datasetIndex === 1
                    ? "withoutTrx"
                    : "disconnected"
                );
                getSlotDetail(data[element[0].index].slotId!);
              }
            },
            onHover: (e, activeElements, chart) => {
              chart.canvas.style.cursor =
                activeElements[0] && shouldOpenDetail ? "pointer" : "default";

              chart.update();
            },
          }}
          data={{
            labels: reducedRegions && reducedRegions.map((r) => r.name),
            datasets: [
              {
                ...dataSetConfig,
                label: datasetLabels[0],
                data: getData("withTransaction"),
                borderRadius: [
                  {
                    bottomLeft: 1000,
                    bottomRight: 1000,
                    topLeft: 1000,
                    topRight: 1000,
                  },
                ],
                inflateAmount: data.map((item) =>
                  item.withTransaction > 0 ? inflateAmount : 0
                ),
                backgroundColor: "#04CE6D",
                datalabels: {
                  display: true,
                  labels: {
                    value: {
                      ...dataSetConfig.datalabels,
                      formatter: (_, context) =>
                        data[context.dataIndex].withTransaction > 0
                          ? data[context.dataIndex].withTransaction
                          : "",
                      align: (context) =>
                        getAlignmet(
                          "withTransaction",
                          context.dataIndex,
                          "align"
                        ),
                      anchor: (context) =>
                        getAlignmet(
                          "withTransaction",
                          context.dataIndex,
                          "anchor"
                        ),
                    },
                    label: {
                      formatter: (_, context) =>
                        reducedRegions && reducedRegions.length > 0
                          ? reducedRegions[context.dataIndex]?.name
                          : null,
                      anchor: "start",
                      align: "end",
                      font: {
                        size: 14,
                        family: "Mulish, sans-serif",
                      },
                      offset: -labelOffset - 25,
                      listeners: {
                        click: (event) => {
                          const filters = shouldOpenDetail
                            ? previousFilters
                            : {
                                regions: data[event.dataIndex].id,
                              };
                          setDashboardFilters(filters);
                          getDashboard(filters);
                        },
                        enter: (ctx) => {
                          ctx.chart.canvas.style.cursor = "pointer";
                        },
                        leave: (ctx) => {
                          ctx.chart.canvas.style.cursor = "default";
                        },
                      },
                    },
                  },
                },
              },
              {
                ...dataSetConfig,
                label: datasetLabels[1],
                data: getData("withoutTransaction"),
                borderRadius: {
                  bottomRight: 1000,
                  topRight: 1000,
                },
                backgroundColor: "#95E1BF",
                inflateAmount: data.map((item) =>
                  item.withoutTransaction > 0 ? inflateAmount : 0
                ),
                datalabels: {
                  ...dataSetConfig.datalabels,
                  formatter: (_, context) =>
                    data[context.dataIndex].withoutTransaction > 0
                      ? data[context.dataIndex].withoutTransaction
                      : "",
                  align: (context) =>
                    getAlignmet(
                      "withoutTransaction",
                      context.dataIndex,
                      "align"
                    ),
                  anchor: (context) =>
                    getAlignmet(
                      "withoutTransaction",
                      context.dataIndex,
                      "anchor"
                    ),
                },
              },
              {
                ...dataSetConfig,
                label: datasetLabels[2],
                data: getData("disconnected"),
                borderRadius: {
                  bottomRight: 1000,
                  topRight: 1000,
                },
                backgroundColor: "#FFA825",
                inflateAmount: data.map((item) =>
                  item.disconnected > 0 ? inflateAmount : 0
                ),
                datalabels: {
                  ...dataSetConfig.datalabels,
                  formatter: (_, context) =>
                    data[context.dataIndex].disconnected > 0
                      ? data[context.dataIndex].disconnected
                      : "",
                  align: (context) =>
                    getAlignmet("disconnected", context.dataIndex, "align"),
                  anchor: (context) =>
                    getAlignmet("disconnected", context.dataIndex, "anchor"),
                },
              },
            ],
          }}
        />
        <Bar
          className="background-chart"
          height="100%"
          options={{
            ...getBarOptions(true),
            animation: {
              duration: 0,
            },
            elements: {
              bar: {
                backgroundColor: "#F5F5F5",
              },
            },
          }}
          data={{
            labels: reducedRegions && reducedRegions.map((r) => r.name),
            datasets: [
              {
                ...dataSetConfig,
                datalabels: {
                  ...dataSetConfig.datalabels,
                  color: "#B8B8B8",
                  formatter: (_, context) => data[context.dataIndex].capacity,
                },
                label: datasetLabels[2],
                data: data.map((_) => 99.99),
                borderRadius: [1000],
                inflateAmount,
              },
            ],
          }}
        />
      </div>
      {shouldOpenDetail && (
        <div
          className="icons-wrapper"
          style={{
            gap: barHeight * 0.9 * 2,
          }}
        >
          {reducedRegions?.map(
            (element, index) =>
              data[index].slotId ? (
                <BasicIcon
                  name="chevron-right"
                  color="#b8b8b8"
                  onClick={() => {
                    setDropdownState("withTrx");
                    getSlotDetail(data[index].slotId!);
                  }}
                />
              ) : <div className="empty-div" />
          )}
        </div>
      )}
    </div>
  ) : (
    <></>
  );
};

const mapStateToProps = (state: RootState) => ({});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setDashboardFilters: (filters: DashboardFilterType) => {
    dispatch(actions.setDashboardFilters(filters));
  },
  getDashboard: (params: DashboardFilterType) => {
    dispatch(actions.getDashboard(params));
  },
  setDropdownState: (params: DashboardDropdownState) => {
    dispatch(modalActions.setDropdownState(params));
  },
  getSlotDetail: (params: number) => {
    dispatch(modalActions.getSlotDetailRequest(params));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(CapacityChart);
